import { useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import { fetchNewBuybacks } from '../api/api';
import type { Buyback } from '../types/buyback';

/** 신규 매입 폴링 주기(ms). 매장 창구 응대에 15초면 충분하고 서버 부하도 가볍다. */
const POLL_MS = 15_000;

/**
 * 스태프용 신규 매입 알람. 고객이 태블릿에서 접수(NEW_ORDER)를 마치면, 직원이 어떤 화면에
 * 있든 알아챌 수 있게 한다. {@link fetchNewBuybacks}(서버 상태 필터 재사용)를 주기적으로
 * 폴링해 새로 들어온 오더가 있으면 알림 팝업을 띄우고, 미처리 건수를 반환해 사이드바
 * 배지로 표시한다.
 *
 * <p>첫 조회는 지금 쌓여 있는 미처리 오더를 baseline 으로만 잡고 알림을 울리지 않는다
 * (새로고침 때마다 기존 건이 다시 울리는 것을 방지). 그 다음 폴링부터 나타난 새 id 만
 * 알람 대상이다.
 *
 * @param enabled  폴링 활성화 여부(매입을 볼 수 있는 컨텍스트에서만 true).
 *                 값이 바뀌면 baseline 을 다시 잡는다(예: SUPER 가 매장을 전환).
 * @param onOpen   알림 클릭 시 호출(매입 관리 페이지로 이동).
 * @returns 현재 미처리(NEW_ORDER) 건수 — 사이드바 배지용.
 */
export function useBuybackAlarm(enabled: boolean, onOpen: () => void): number {
  const { notification } = App.useApp();
  const [pendingCount, setPendingCount] = useState(0);

  // null = 아직 baseline(첫 조회)을 잡지 않음.
  const seenRef = useRef<Set<number> | null>(null);
  // onOpen 을 effect 의존성에서 빼기 위해 ref 로 최신값을 참조한다.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!enabled) {
      setPendingCount(0);
      seenRef.current = null; // 비활성 → 다시 켜질 때 baseline 부터.
      return;
    }

    let cancelled = false;

    const notify = (fresh: Buyback[]) => {
      const first = fresh[0];
      const who = first.name ?? '고객';
      const mgmt = first.managementNumber ?? `#${first.id}`;
      notification.open({
        message: fresh.length === 1 ? '신규 매입 접수' : `신규 매입 ${fresh.length}건 접수`,
        description: fresh.length === 1
          ? `${who} 님 (${mgmt}) 접수되었습니다.`
          : `${who} 님 외 ${fresh.length - 1}건이 접수되었습니다.`,
        placement: 'topRight',
        duration: 0, // 직원이 직접 닫거나 클릭할 때까지 유지(알람 성격).
        onClick: () => { onOpenRef.current(); notification.destroy(); },
        style: { cursor: 'pointer' },
      });
    };

    const poll = async () => {
      try {
        const list = await fetchNewBuybacks();
        if (cancelled) return;
        setPendingCount(list.length);

        const ids = new Set(list.map((b) => b.id));
        if (seenRef.current === null) {
          seenRef.current = ids; // 첫 조회: baseline 만 잡고 알림 없음.
          return;
        }
        const fresh = list.filter((b) => !seenRef.current!.has(b.id));
        seenRef.current = ids;
        if (fresh.length > 0) notify(fresh);
      } catch {
        /* 401 등은 http 인터셉터가 처리. 폴링은 조용히 다음 주기로 넘어간다. */
      }
    };

    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [enabled, notification]);

  return pendingCount;
}
